'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { fadeInLeft, fadeInRight, staggerContainer, viewportOnce } from '@/lib/animations';

export default function ForBanks() {
  const t = useTranslations('ForBanks');

  const bullets = [
    { id: 'p2p', key: 'bullet_1' },
    { id: 'brand', key: 'bullet_2' },
    { id: 'tips', key: 'bullet_3' },
    { id: 'empsa', key: 'bullet_4' },
  ];

  return (
    <section id="for-banks" className="w-full overflow-x-clip bg-[#F8FAFC] py-16 sm:py-24 px-6 sm:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

        <motion.div
          className="lg:col-span-6 space-y-6"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer(0.1)}
        >
          <motion.span variants={fadeInLeft} className="text-[10px] sm:text-xs font-bold tracking-widest color-primary uppercase block">
            {t('badge')}
          </motion.span>

          <motion.h2 variants={fadeInLeft} id="for-banks-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-[1.12]">
            {t('heading')}
          </motion.h2>

          <motion.p variants={fadeInLeft} className="text-xs sm:text-sm text-gray-500 font-normal leading-relaxed max-w-xl">
            {t('description')}
          </motion.p>

          <motion.ul variants={staggerContainer(0.08)} className="space-y-3 pt-2">
            {bullets.map((item) => (
              <motion.li variants={fadeInLeft} key={item.id} className="flex items-center gap-3 text-xs sm:text-sm font-semibold text-gray-700">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                <span>{t(item.key)}</span>
              </motion.li>
            ))}
          </motion.ul>

          {/*/Removed temporarely, no link where to redirect  /*}
          {/* <div className="pt-4">
            <a
              href="#bank-partnership"
              className="inline-flex items-center justify-center gap-2 bg-[#212529] hover:bg-black text-white font-bold text-xs sm:text-sm px-6 py-3.5 rounded-xl transition-all shadow-md group"
            >
              <span>{t('cta_button')}</span>
              <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div> */}
        </motion.div>

        <motion.div
          className="lg:col-span-6 space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer(0.15, 0.1)}
        >

          <motion.div variants={fadeInRight} className="relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 overflow-hidden">
            <div className="absolute top-0 left-0 bottom-0 w-1 bg-red-500 rounded-l-2xl" />

            <div className="flex items-center justify-between pb-5 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl overflow-hidden shrink-0 shadow-sm border border-gray-100">
                  <img
                    src="https://flagcdn.com/al.svg"
                    alt="Albania flag"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                  {t('bank_1_title')}
                </h3>
              </div>

              <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-500 uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>{t('status_live')}</span>
              </div>
            </div>

            <div className="pt-4 text-center">
              <span className="text-[11px] font-medium text-gray-400 tracking-wider">
                {t('bank_features')}
              </span>
            </div>
          </motion.div>

          <motion.div variants={fadeInRight} className="relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 overflow-hidden">
            <div className="absolute top-0 left-0 bottom-0 w-1 bg-red-500 rounded-l-2xl" />

            <div className="flex items-center justify-between pb-5 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl overflow-hidden shrink-0 shadow-sm border border-gray-100">
                  <img
                    src="https://flagcdn.com/xk.svg"
                    alt="Kosovo flag"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                  {t('bank_2_title')}
                </h3>
              </div>

              <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-500 uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>{t('status_live')}</span>
              </div>
            </div>

            <div className="pt-4 text-center">
              <span className="text-[11px] font-medium text-gray-400 tracking-wider">
                {t('bank_features')}
              </span>
            </div>
          </motion.div>

          <motion.div variants={fadeInRight} className="bg-white/60 border-2 border-dashed border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between pb-5 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center color-primary shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                  {t('bank_3_title')}
                </h3>
              </div>

              <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-slate-300" />
                <span>{t('status_ready')}</span>
              </div>
            </div>

            <div className="pt-4 flex items-center justify-center gap-2 text-xs font-semibold text-gray-500">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" strokeWidth="1.5" />
                <path strokeLinecap="round" strokeWidth="1.5" d="M12 7v5l3 3" />
              </svg>
              <span>{t('integration_time')}</span>
            </div>
          </motion.div>

        </motion.div>

      </div>
    </section>
  );
}